import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  orders: [],
  currentOrder: null,
  statusCounts: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  filters: {
    status: '',
    search: '',
    machine: '',
    priority: '',
  },
  loading: false,
  error: null,
};

// Async thunks
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/orders', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Orders laden mislukt');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Order laden mislukt');
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateStatus',
  async ({ id, status, note }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/orders/${id}/status`, { status, note });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Status update mislukt');
    }
  }
);

export const assignOrderToMachine = createAsyncThunk(
  'orders/assignMachine',
  async ({ orderId, machineId }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/orders/${orderId}/assign`, { machineId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Toewijzing mislukt');
    }
  }
);

export const syncOrderFromPicqer = createAsyncThunk(
  'orders/syncFromPicqer',
  async (picqerId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/picqer/sync/order/${picqerId}`);
      return response.data.order;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Sync met Picqer mislukt');
    }
  }
);

export const toggleSyncLock = createAsyncThunk(
  'orders/toggleSyncLock',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/orders/${id}/sync-lock`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Sync lock toggle mislukt');
    }
  }
);

export const replaceItemProduct = createAsyncThunk(
  'orders/replaceItemProduct',
  async ({ orderId, itemId, productId }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/orders/${orderId}/items/${itemId}/product`, { productId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Product vervangen mislukt');
    }
  }
);

export const deleteAllOrders = createAsyncThunk(
  'orders/deleteAll',
  async ({ status } = {}, { rejectWithValue }) => {
    try {
      const params = { confirm: 'true' };
      if (status) params.status = status;
      const response = await api.delete('/orders/all', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Verwijderen mislukt');
    }
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
        state.statusCounts = action.payload.statusCounts || {};
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single order
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update status
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      // Assign to machine
      .addCase(assignOrderToMachine.fulfilled, (state, action) => {
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      // Sync from Picqer
      .addCase(syncOrderFromPicqer.pending, (state) => {
        state.loading = true;
      })
      .addCase(syncOrderFromPicqer.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(syncOrderFromPicqer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Toggle sync lock
      .addCase(toggleSyncLock.fulfilled, (state, action) => {
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      // Replace item product
      .addCase(replaceItemProduct.fulfilled, (state, action) => {
        state.currentOrder = action.payload;
        const index = state.orders.findIndex((o) => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      // Delete all orders
      .addCase(deleteAllOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAllOrders.fulfilled, (state) => {
        state.loading = false;
        state.orders = [];
        state.pagination.total = 0;
      })
      .addCase(deleteAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, setPage, clearCurrentOrder } = ordersSlice.actions;

// Alias for fetchOrderById to be used as fetchOrder
export const fetchOrder = fetchOrderById;

export default ordersSlice.reducer;
